import { useCallback, useRef, useState } from 'react';
import {
  getAttachmentAccept,
  validateAttachmentFile,
} from '../utils/attachmentConstraints';

export default function useChatComposer({ onSendMessage, shouldStickToBottomRef }) {
  const fileInputRef = useRef(null);
  const attachButtonRef = useRef(null);
  const expectedKindRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);

  const stickToBottom = useCallback(() => {
    if (shouldStickToBottomRef) {
      shouldStickToBottomRef.current = true;
    }
  }, [shouldStickToBottomRef]);

  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    setFileError(null);
  }, []);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    const expectedKind = expectedKindRef.current;
    expectedKindRef.current = null;
    event.target.value = '';

    if (!file) return;

    const validation = validateAttachmentFile(file, expectedKind);
    if (!validation.valid) {
      setSelectedFile(null);
      setFileError(validation.message);
      return;
    }

    setFileError(null);
    setSelectedFile(file);
  }, []);

  const openFilePicker = useCallback((accept, expectedKind = null) => {
    if (!fileInputRef.current) return;
    expectedKindRef.current = expectedKind;
    fileInputRef.current.accept = accept;
    fileInputRef.current.click();
  }, []);

  const openAttachmentPicker = useCallback(
    (kind) => {
      openFilePicker(getAttachmentAccept(kind), kind);
    },
    [openFilePicker]
  );

  const openAttachMenu = useCallback(() => setAttachMenuOpen(true), []);
  const closeAttachMenu = useCallback(() => setAttachMenuOpen(false), []);

  const handleSend = useCallback(() => {
    stickToBottom();

    if (selectedFile) {
      onSendMessage(selectedFile);
      setSelectedFile(null);
      setFileError(null);
    } else {
      onSendMessage();
    }
  }, [onSendMessage, selectedFile, stickToBottom]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleVoiceComplete = useCallback(
    (file) => {
      const validation = validateAttachmentFile(file, 'audio');
      if (!validation.valid) {
        setFileError(validation.message);
        return;
      }

      stickToBottom();
      setFileError(null);
      onSendMessage(file);
    },
    [onSendMessage, stickToBottom]
  );

  return {
    fileInputRef,
    attachButtonRef,
    selectedFile,
    fileError,
    clearFileError: () => setFileError(null),
    voiceDialogOpen,
    attachMenuOpen,
    openVoiceDialog: () => setVoiceDialogOpen(true),
    closeVoiceDialog: () => setVoiceDialogOpen(false),
    openAttachMenu,
    closeAttachMenu,
    handleFileSelect,
    openFilePicker,
    openAttachmentPicker,
    clearSelectedFile,
    handleSend,
    handleKeyDown,
    handleVoiceComplete,
  };
}
